import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from '@material-ui/core/Button';
import s from './WidgetPage.css';
import Link from '../../../components/Link';
import StagedLink from '../../../components/StagedLink';
import WidgetIcon from '../../../components/WidgetIcon';
import { getRelated } from '../../../data/widgets';
import { CHROME_STORE_URL } from '../../../utils/seo';

const PLAN_LABEL = {
	free: 'Free',
	pro: 'PRO',
	mixed: 'Free, with PRO options',
};

const PLAN_CLASS = {
	free: s.pillFree,
	pro: s.pillPro,
	mixed: s.pillFree,
};

/**
 * Лендинг одного виджета: /widgets/:slug
 *
 * Разметка сознательно простая и плоская: один h1 (в HeadMessage выше),
 * дальше только h2/h3 без пропусков уровней.
 */
/*
 * Скриншоты лежат в public/img/widgets и едут внутри образа. Путь /img,
 * а не /screenshots: последний перехватывает nginx и отдаёт из тома
 * с превью тем. @see DEPLOY.md
 */
/**
 * Атрибуты картинки. Если у кадра есть версия шириной 720 px
 * (`<имя>-720.webp`, флаг `small: true` в каталоге), телефоны получают её
 * через srcset, а не полноразмерный кадр 1440 px. @see task_screenshots.md §5
 */
function imageProps(image, fallbackWidth, fallbackHeight) {
	const src = `/img/widgets/${image.file}`;
	const props = {
		src,
		alt: image.alt,
		width: image.width || fallbackWidth,
		height: image.height || fallbackHeight,
		loading: 'lazy',
	};

	if (image.small) {
		const small = src.replace(/\.webp$/, '-720.webp');
		props.srcSet = `${small} 720w, ${src} 1440w`;
		props.sizes = '(max-width: 800px) 100vw, 760px';
	}

	return props;
}

function WidgetPage({ widget }) {
	const related = getRelated(widget);
	const planLabel = PLAN_LABEL[widget.plan] || PLAN_LABEL.free;

	return (
		<div className={s.root}>
			<div className={s.meta}>
				<WidgetIcon slug={widget.slug} category={widget.category} />
				<span className={PLAN_CLASS[widget.plan] || s.pillFree}>
					{planLabel}
				</span>
				<span className={s.pill}>{widget.category} widget</span>
			</div>

			<p className={s.lead}>{widget.lead}</p>

			{widget.screenshot && (
				<figure className={s.figure}>
					<img
						alt={widget.screenshot.alt}
						{...imageProps(widget.screenshot, 720, 450)}
					/>
					{widget.screenshot.caption && (
						<figcaption>{widget.screenshot.caption}</figcaption>
					)}
				</figure>
			)}

			{widget.sections.map(section => (
				<section key={section.h2}>
					<h2>{section.h2}</h2>
					{(section.p || []).map(paragraph => (
						<p key={paragraph.slice(0, 40)}>{paragraph}</p>
					))}
					{section.ul && (
						<ul className={s.useCases}>
							{section.ul.map(item => (
								<li key={item}>{item}</li>
							))}
						</ul>
					)}
					{section.image && (
						<figure className={s.figure}>
							<img
								alt={section.image.alt}
								{...imageProps(section.image, 1440, 900)}
							/>
							{section.image.caption && (
								<figcaption>{section.image.caption}</figcaption>
							)}
						</figure>
					)}
				</section>
			))}

			{widget.settings && widget.settings.length > 0 && (
				<section>
					<h2>{widget.name} settings</h2>
					{/* Как секция в окне настроек расширения: карточка со строками */}
					<dl className={s.settings}>
						{widget.settings.map(setting => (
							<div className={s.settingsRow} key={setting.name}>
								<dt>{setting.name}</dt>
								<dd>{setting.detail}</dd>
							</div>
						))}
					</dl>
				</section>
			)}

			{widget.useCases && widget.useCases.length > 0 && (
				<section>
					<h2>What people use it for</h2>
					<ul className={s.useCases}>
						{widget.useCases.map(useCase => (
							<li key={useCase}>{useCase}</li>
						))}
					</ul>
				</section>
			)}

			<div className={s.cta}>
				<WidgetIcon
					slug={widget.slug}
					category={widget.category}
					size="large"
				/>
				<p className={s.ctaTitle}>
					Put the {widget.name.toLowerCase()} on your new tab
				</p>
				<p className={s.ctaText}>
					It comes with New Tab Studio. The extension is free to
					install and needs no account to start.
				</p>
				<ol className={s.steps}>
					<li>
						<span className={s.num}>1</span>
						Add New Tab Studio to Chrome
					</li>
					<li>
						<span className={s.num}>2</span>
						Open a new tab, click the button at the bottom left
					</li>
					<li>
						<span className={s.num}>3</span>
						Pick the widget, then drag it into place
					</li>
				</ol>
				<Button
					href={CHROME_STORE_URL}
					color="primary"
					variant="contained"
					size="large"
				>
					Add to Chrome. It’s free
				</Button>
			</div>

			{widget.faq && widget.faq.length > 0 && (
				<section className={s.faq}>
					<h2>
						Questions about the {widget.name.toLowerCase()} widget
					</h2>
					{widget.faq.map(item => (
						<div className={s.faqItem} key={item.q}>
							<h3>{item.q}</h3>
							<p>{item.a}</p>
						</div>
					))}
				</section>
			)}

			{related.length > 0 && (
				<section className={s.related}>
					<h2>Widgets that pair well with it</h2>
					<ul className={s.relatedList}>
						{related.map(item => (
							<li key={item.slug}>
								<Link to={`/widgets/${item.slug}`}>
									<WidgetIcon
										slug={item.slug}
										category={item.category}
									/>
									<span>{item.name} widget</span>
								</Link>
							</li>
						))}
					</ul>
					<p className={s.keywordNote}>
						See the{' '}
						<StagedLink to="/widgets">
							full widget catalogue
						</StagedLink>{' '}
						or browse{' '}
						<Link to="/themes">themes other people have built</Link>
						.
					</p>
				</section>
			)}
		</div>
	);
}

WidgetPage.propTypes = {
	// Форма объекта описана в src/data/widgets/catalog/*.js — здесь держать
	// её копию смысла нет, она разъедется с каталогом на первой же правке.
	widget: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default withStyles(s)(WidgetPage);
